import { t } from "i18next";
import * as z from "zod";

import { WALLET_KYC_LEVELS } from "@/features/shared/api/enums";

/**
 * Schéma du formulaire de création d'un dossier KYC (CreateKycCaseRequestDto)
 * — étape 4 du parcours d'inscription. `assignedAgentId` vide = canal
 * SELF_SERVICE ; renseigné = AGENT_ASSISTED (bascule côté serveur).
 *
 * Factory (règle projet) : créé via `createKycCaseSchema()` et mémoïsé au
 * niveau composant sur la langue courante — jamais d'instance exportée.
 */
export const createKycCaseSchema = () =>
  z.object({
    partyId: z
      .string()
      .trim()
      .min(1, { message: t("requiredFieldError") })
      .uuid({ message: t("invalidFieldError") }),
    levelRequested: z.enum(WALLET_KYC_LEVELS, {
      errorMap: () => ({ message: t("requiredFieldError") }),
    }),
    assignedAgentId: z
      .string()
      .trim()
      .uuid({ message: t("invalidFieldError") })
      .or(z.literal(""))
      .optional(),
  });

export type KycCaseFormValues = z.infer<ReturnType<typeof createKycCaseSchema>>;

/**
 * Schéma du formulaire de revue d'un dossier KYC (ReviewKycCaseRequestDto) :
 * decision APPROVED ou REJECTED ; un rejet DOIT être motivé (notes
 * obligatoire si REJECTED).
 */
export const createKycCaseReviewSchema = () =>
  z
    .object({
      decision: z.enum(["APPROVED", "REJECTED"], {
        errorMap: () => ({ message: t("requiredFieldError") }),
      }),
      notes: z.string().trim().or(z.literal("")).optional(),
    })
    .superRefine((values, ctx) => {
      if (values.decision === "REJECTED" && !values.notes?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["notes"],
          message: t("requiredFieldError"),
        });
      }
    });

export type KycCaseReviewFormValues = z.infer<
  ReturnType<typeof createKycCaseReviewSchema>
>;
